// YERİ: src/app/domains/site/sections/anchor-nav.section.ts
//
// Tek sayfa (single page) gezinme çubuğu — sayfa içi çapalara götürür.
//
// ── NEDEN AYRI BİR BÖLÜM ────────────────────────────────────────────────────
// Mevcut `menu-bar` bölümü menüsünü VERİTABANINDAN (ClientWebMenu) okuyor;
// şablon uygulanırken menü kayıtları oluşturulmadığı için tek sayfa şablonunda
// boş çıkardı. Bu bölüm bağlantılarını kendi config'inden alır: şablon
// uygulanır uygulanmaz çalışır, müşteri isterse editörden değiştirir.
//
// ── KAYDIRMA NEDEN ELLE ─────────────────────────────────────────────────────
// Tarayıcının kendi çapa atlaması bölümü sayfanın en üstüne yapıştırıyor ve
// yapışkan çubuk başlığın üzerine biniyor. Burada hedefin konumundan çubuk
// yüksekliği düşülerek kaydırılıyor.

import { Component, ElementRef, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { SiteConfig, cfg } from './section.model';

interface NavLink { label: string; anchor: string }

@Component({
  selector: 'sec-anchor-nav',
  standalone: true,
  template: `
    <nav #bar
         class="sticky top-0 z-40 border-b border-black/5 backdrop-blur"
         style="background-color:color-mix(in srgb, var(--site-primary) 92%, transparent)">
      <div class="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3 lg:px-8">
        @if (brand()) {
          <a href="#" class="shrink-0 font-[family-name:var(--site-head-font)] text-base font-bold text-white"
             (click)="basaDon($event)">{{ brand() }}</a>
        }

        <!-- Dar ekranda bağlantılar yatay kayar: menüyü katlayıp hamburger
             açmak tek sayfada gereksiz, hepsi zaten aynı sayfada. -->
        <div class="ws-navrail -mx-1 flex flex-1 items-center gap-1 overflow-x-auto px-1">
          @for (l of links(); track $index) {
            <a [href]="'#' + l.anchor"
               class="ws-btn shrink-0 px-3 py-1.5 text-sm font-medium transition"
               [class]="aktif() === l.anchor ? 'bg-white/20 text-white' : 'text-white/75 hover:bg-white/10 hover:text-white'"
               (click)="git($event, l.anchor)">{{ l.label }}</a>
          }
        </div>

        @if (ctaText()) {
          <a [href]="ctaUrl()" (click)="git($event, ctaAnchor())"
             class="ws-btn shrink-0 px-4 py-1.5 text-sm font-semibold shadow"
             [class]="fxClass()"
             style="background-color:var(--site-accent); color:#111">{{ ctaText() }}</a>
        }
      </div>
    </nav>
  `,
  styles: [`
    /* Kaydırma çubuğu gizli — çubuk ince, çubuğun çubuğu görünmesin. */
    .ws-navrail { scrollbar-width: none; -ms-overflow-style: none; }
    .ws-navrail::-webkit-scrollbar { display: none; }
  `],
})
export class AnchorNavSection implements OnDestroy {
  config = input<SiteConfig>({});
  /**
   * Önizleme (şablon kartı / editör).
   *
   * Kartta gezinme anlamsız — ve pahalı: galeri sayfasında kırk dört önizleme
   * yan yana çiziliyor, her biri kendi gözlemcisini kurunca sekme donuyordu.
   * Önizlemede çubuk yalnız GÖRÜNÜR, gözlemci kurulmaz, tıklama yutulur.
   */
  preview = input<boolean>(false);

  private host = inject(ElementRef<HTMLElement>);

  protected brand = computed(() => cfg(this.config(), 'brand', ''));
  protected ctaText = computed(() => cfg(this.config(), 'ctaText', ''));
  protected ctaUrl = computed(() => cfg(this.config(), 'ctaUrl', '#contact'));
  protected ctaAnchor = computed(() => this.ctaUrl().replace('#', ''));

  protected fxClass = computed(() => {
    const fx = String(cfg(this.config(), 'buttonEffect', ''));
    return fx ? 'ws-fx-' + fx : '';
  });

  protected links = computed<NavLink[]>(() => {
    const raw = this.config()['links'];
    const list = Array.isArray(raw) ? raw.filter((l: NavLink) => l?.label && l?.anchor) : [];
    return list.length ? list : [
      { label: 'Hakkımızda', anchor: 'about' },
      { label: 'Hizmetler', anchor: 'services' },
      { label: 'İletişim', anchor: 'contact' },
    ];
  });

  /** O an ekranda olan bölümün çapası — bağlantı vurgusu için. */
  protected aktif = signal<string>('');

  private observer: IntersectionObserver | null = null;

  constructor() {
    // Gözlemci, bağlantı listesi hazır olduktan SONRA kurulmalı; hedefler
    // sayfada henüz çizilmemiş olabileceği için ilk kurulum ertelenir.
    queueMicrotask(() => { if (!this.preview()) this.gozle(); });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private gozle(): void {
    if (typeof IntersectionObserver === 'undefined' || typeof document === 'undefined') return;

    const hedefler = this.links()
      .map(l => document.getElementById(l.anchor))
      .filter((e): e is HTMLElement => !!e);
    if (!hedefler.length) return;

    // Gözlemcinin kökü de kaydıran kap olmalı: önizlemede kök pencere
    // sanılırsa hiçbir bölüm "görünür" sayılmaz, vurgu hiç değişmez.
    const kok = this.kaydiranKap();

    this.observer = new IntersectionObserver(
      entries => {
        // Ekranın üst yarısına en çok giren bölüm "aktif" sayılır; birden çok
        // bölüm aynı anda görünürken en üsttekini seçmek gerekiyor.
        const gorunur = entries.filter(e => e.isIntersecting);
        if (gorunur.length) {
          const enUst = gorunur.reduce((a, b) =>
            a.boundingClientRect.top <= b.boundingClientRect.top ? a : b);
          this.aktif.set(enUst.target.id);
        }
      },
      { root: kok, rootMargin: '-20% 0px -70% 0px', threshold: 0 },
    );
    hedefler.forEach(h => this.observer!.observe(h));
  }

  protected git(ev: Event, anchor: string): void {
    // Önizlemede tıklama yutulur: kartın içinde kaydırmak, kartı değil sayfayı
    // oynatır ve galeri zıplar.
    if (this.preview()) { ev.preventDefault(); return; }
    if (!anchor) return;
    const hedef = typeof document !== 'undefined' ? document.getElementById(anchor) : null;
    if (!hedef) return;               // hedef yoksa tarayıcı davranışına bırak
    ev.preventDefault();

    // Yapışkan çubuğun yüksekliği kadar yukarıdan başla; aksi hâlde bölüm
    // başlığı çubuğun altında kalıyor.
    const bar = (this.host.nativeElement as HTMLElement).querySelector('nav');
    const offset = (bar ? bar.getBoundingClientRect().height : 0) + 8;

    const kap = this.kaydiranKap();
    if (kap) {
      // Önizlemede sayfa PENCEREDE değil, kendi kabında kayıyor; window.scrollTo
      // orada hiçbir şey yapmıyordu (şablon kartı ve editör önizlemesi).
      const kapRect = kap.getBoundingClientRect();
      const y = hedef.getBoundingClientRect().top - kapRect.top + kap.scrollTop - offset;
      kap.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      const y = hedef.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    this.aktif.set(anchor);
  }

  protected basaDon(ev: Event): void {
    ev.preventDefault();
    if (this.preview()) return;
    const kap = this.kaydiranKap();
    if (kap) kap.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
    this.aktif.set('');
  }

  /**
   * Kaydırmayı yapan ata eleman; pencere kaydırıyorsa null.
   * Canlı sitede pencere kaydırır, önizlemede bir div kaydırır.
   */
  private kaydiranKap(): HTMLElement | null {
    if (typeof window === 'undefined') return null;
    let el = (this.host.nativeElement as HTMLElement).parentElement;
    while (el && el !== document.body) {
      const st = getComputedStyle(el);
      if (/(auto|scroll)/.test(st.overflowY) && el.scrollHeight > el.clientHeight + 4) return el;
      el = el.parentElement;
    }
    return null;
  }
}
